<template>
	<span v-if="requirements.length || freeform">
		<b-button
			size="sm"
			:variant="freeform ? 'secondary' : (allConfigured ? 'success' : 'secondary')"
			v-b-modal="modalId"
			title="Clés de sécurité de cette étape">
			<i class="fas fa-key"></i>
		</b-button>
		<b-modal
			:id="modalId"
			title="Clés de sécurité de l'étape"
			size="xl"
			hide-footer>
			<b-alert v-if="freeform" show variant="secondary" class="mb-3">
				Cette étape peut référencer n'importe quelle clé enregistrée. Toutes les clés existantes sont
				listées ci-dessous ; vous pouvez également en ajouter une nouvelle.
			</b-alert>

			<b-form-group v-if="freeform" label="Ajouter une clé" class="mb-3">
				<b-input-group>
					<b-form-input
						v-model="new_name"
						placeholder="Nom de la clé (ex: mon_api_key)" />
					<b-form-input
						:type="new_value_visible ? 'text' : 'password'"
						v-model="new_value"
						placeholder="Valeur"
						@keyup.enter="add_custom_credential" />
					<b-form-select v-model="new_scope" :options="scope_options" style="max-width: 200px" />
					<template #append>
						<b-button
							variant="outline-secondary"
							@click="new_value_visible = !new_value_visible"
							><i :class="new_value_visible ? 'fas fa-eye' : 'fas fa-eye-slash'"></i
						></b-button>
						<b-button
							variant="primary"
							:disabled="!new_name.trim() || !new_value || adding"
							@click="add_custom_credential"
							>Ajouter</b-button
						>
					</template>
				</b-input-group>
			</b-form-group>

			<h6 class="text-muted mb-2"><i class="fas fa-users mr-1"></i>Clés communes (client)</h6>
			<step_credential_field
				v-for="req in display_requirements_client"
				:key="'client-' + req.name"
				:credential-name="req.name"
				:label="req.label"
				scope="client"
				@status="on_status" />

			<hr class="my-3" />

			<h6 class="text-muted mb-2"><i class="fas fa-user mr-1"></i>Clés personnelles (moi)</h6>
			<step_credential_field
				v-for="req in display_requirements_user"
				:key="'user-' + req.name"
				:credential-name="req.name"
				:label="req.label"
				scope="user"
				@status="on_status" />
		</b-modal>
	</span>
</template>

<script>
	import step_credential_field from "./StepCredentialField.vue";
	import credentials from "../services/credentials";
	import { STEP_CREDENTIAL_REQUIREMENTS } from "../config/step_credentials_fields";

	// Table de correspondance nom -> label, construite à partir de tout le registre,
	// pour proposer un libellé lisible même en mode "freeform" (api / script personnalisé).
	var KNOWN_LABELS = {};
	Object.keys(STEP_CREDENTIAL_REQUIREMENTS).forEach(function (action) {
		STEP_CREDENTIAL_REQUIREMENTS[action].forEach(function (req) {
			KNOWN_LABELS[req.name] = req.label;
		});
	});

	export default {
		name: "step_credentials_button",
		components: { step_credential_field },
		props: {
			requirements: {
				type: Array,
				default: () => [],
			},
			/** Si vrai, affiche toutes les clés existantes (pas seulement `requirements`) et permet d'en ajouter. */
			freeform: {
				type: Boolean,
				default: false,
			},
			modalId: {
				type: String,
				required: true,
			},
		},
		data() {
			return {
				// statuses[name] = { client: 'configured'|'absent'|'loading', user: 'configured'|'absent'|'loading' }
				statuses: {},
				// Noms des clés freeform réellement trouvées (getAll()), par scope où elles existent.
				client_credential_names: [],
				user_credential_names: [],
				new_name: "",
				new_value: "",
				new_value_visible: false,
				new_scope: "client",
				scope_options: [
					{ value: "client", text: "Client (commune)" },
					{ value: "user", text: "Moi (personnelle)" },
				],
				adding: false,
			};
		},
		computed: {
			/**
			 * En mode freeform, on n'affiche pour chaque scope QUE les clés effectivement configurées
			 * dans ce scope (le scope est déterminé par la clé elle-même, pas choisi ici) + les clés
			 * fixes du registre.
			 */
			display_requirements_client() {
				return this.build_display_list(this.client_credential_names);
			},
			display_requirements_user() {
				return this.build_display_list(this.user_credential_names);
			},
			allConfigured() {
				var all_names = this.display_requirements_client.concat(this.display_requirements_user).map((r) => r.name);
				var seen = {};
				var unique_names = all_names.filter((n) => (seen[n] ? false : (seen[n] = true)));
				return (
					unique_names.length > 0 &&
					unique_names.every((name) => {
						var s = this.statuses[name];
						return s && (s.client === "configured" || s.user === "configured");
					})
				);
			},
		},
		watch: {
			requirements: {
				immediate: true,
				handler() {
					this.refresh_all();
				},
			},
		},
		methods: {
			build_display_list(scoped_names) {
				if (!this.freeform) return this.requirements;

				var names = this.requirements.map((r) => r.name).concat(scoped_names);
				var seen = {};
				var result = [];
				names.forEach(function (name) {
					if (!name || seen[name]) return;
					seen[name] = true;
					result.push({ name: name, label: KNOWN_LABELS[name] || name });
				});
				return result;
			},
			add_custom_credential: async function () {
				var name = this.new_name.trim();
				if (!name || !this.new_value || this.adding) return;

				this.adding = true;
				try {
					await credentials.post(name, this.new_value, this.new_scope === "user");
					this.new_name = "";
					this.new_value = "";
					this.new_value_visible = false;
					await this.refresh_all();
				} finally {
					this.adding = false;
				}
			},
			on_status({ name, scope, status }) {
				var current = this.statuses[name] || { client: "absent", user: "absent" };
				current[scope] = status;
				this.$set(this.statuses, name, current);
			},
			refresh_all: async function () {
				var all = await credentials.getAll();

				if (this.freeform) {
					var client_names = {};
					var user_names = {};
					all.forEach(function (c) {
						if (c.client_user_uid) user_names[c.name] = true;
						else client_names[c.name] = true;
					});
					this.client_credential_names = Object.keys(client_names);
					this.user_credential_names = Object.keys(user_names);
				}

				var reqs = this.freeform
					? this.display_requirements_client.concat(this.display_requirements_user)
					: this.requirements;
				if (!reqs.length) return;

				var seen = {};
				for (var i = 0; i < reqs.length; i++) {
					var req = reqs[i];
					if (seen[req.name]) continue;
					seen[req.name] = true;
					var client_found = all.find((c) => c.name === req.name && !c.client_user_uid);
					var user_found = all.find((c) => c.name === req.name && !!c.client_user_uid);

					this.$set(this.statuses, req.name, {
						client: client_found ? "configured" : "absent",
						user: user_found ? "configured" : "absent",
					});
				}
			},
		},
	};
</script>
